import type { EntityState } from '@ngrx/entity';
import type { Action, ActionCreator, Creator, NotAllowedCheck } from '@ngrx/store';
import type { ProjectResourceType } from 'core/http/project/resource-updates/resource-updates.service';
import type { Observable } from 'rxjs';
import { createAction, props } from '@ngrx/store';

type CustomActionCreator<T extends string, P extends object> = ActionCreator<T, (props: P & NotAllowedCheck<P>) => P & Action<T>>;

export interface CompletedEntityState<T> extends EntityState<T> {
  loaded: boolean;
  loading: boolean;
  error: Error | null;
}

export const initialCompletedEntityState = {
  loaded: false,
  loading: false,
  error: null,
};

export interface SetStateProperties<
  EntityType,
  AType extends string = string,
  BType extends string = string,
  CType extends string = string,
  AAction extends Creator = Creator<[{ value: boolean }], { type: AType }>,
  BAction extends Creator = Creator<[{ value: Error | null }], { type: BType }>,
> {
  setLoading: ActionCreator<AType, AAction>;
  setLoaded: ActionCreator<CType, () => Action<CType>>;
  setError: ActionCreator<BType, BAction>;
  request: () => Observable<EntityType>;
  cacheKey?: ProjectResourceType;
}

export function createProperties<
  EntityType,
  AType extends string = string,
  BType extends string = string,
  CType extends string = string,
  AAction extends Creator = Creator<[{ value: boolean }], { type: AType }>,
  BAction extends Creator = Creator<[{ value: Error | null }], { type: BType }>,
>(
  setLoading: ActionCreator<AType, AAction>,
  setLoaded: ActionCreator<CType, () => Action<CType>>,
  setError: ActionCreator<BType, BAction>,
  request: () => Observable<EntityType>,
  cacheKey?: ProjectResourceType,
): SetStateProperties<EntityType, AType, BType, CType, AAction, BAction> {
  return {
    setLoading,
    setLoaded,
    setError,
    request,
    cacheKey,
  };
}

export function createSetLoadingAction<T extends string>(type: T): CustomActionCreator<T, { value: boolean }> {
  return createAction(type, props<{ value: boolean }>());
}

export function createSetErrorAction<T extends string>(type: T): CustomActionCreator<T, { value: Error | null }> {
  return createAction(type, props<{ value: Error | null }>());
}
